import React, { useState, useEffect, useCallback } from "react"
import { twJoin } from "tailwind-merge"
import { Link } from "gatsby"
import { FORM_ROUTES } from "@/constants/routes"
import { useAuth } from "@/auth/Auth"
import { IoIosContrast } from "react-icons/io"
import FontIcon from "@/landing-page/components/icons/font"
import { trackEvent } from "@/helpers/ga"

export function useChangeFontSize() {
  const [fontSize, setFontSize] = useState<number>(1)

  const changeFontSize = useCallback(() => {
    setFontSize((prevFontSize) => {
      if (prevFontSize == 1) {
        return 1.2
      } else if (prevFontSize == 1.2) {
        return 1.3
      } else {
        return 1
      }
    })
  }, [])

  useEffect(() => {
    if (fontSize == 1.2) {
      document.documentElement.classList.add("text-[1.2em]")
    } else if (fontSize == 1.3) {
      document.documentElement.classList.add("text-[1.3em]")
    } else {
      document.documentElement.classList.remove("text-[1.2em]", "text-[1.3em]")
    }
  }, [fontSize])

  return changeFontSize
}

export function Header() {
  const { session, logout } = useAuth()
  const user = session?.tokens?.idToken?.payload as { name: string }

  const [open, setOpen] = useState<boolean>(false)
  const [btnActive, setBtnActive] = useState<boolean>(false)

  const onDarkMode = useCallback(() => {
    setBtnActive((active) => !active)
    document.body.classList.toggle("dark")
  }, [])

  const changeFontSize = useChangeFontSize()

  return (
    <header className="w-full fixed lg:top-10 flex justify-center z-50">
      <nav
        className={twJoin(
          open ? "right-0 top-0" : "lg:right-auto -right-full",
          "lg:relative fixed bg-green-dark lg:rounded-full lg:w-auto w-full lg:h-auto h-screen lg:py-2 py-8 lg:px-12 overflow-x-hidden transition-all duration-300 ease-in-out dark:bg-green-dark2",
        )}
      >
        <ul className="flex lg:justify-around lg:flex-row flex-col items-center gap-8 lg:w-auto w-full lg:px-0 px-8">
          <li className="lg:w-auto w-full text-center">
            <Link
              to="/"
              title="Início"
              className="text-white lg:text-base text-lg font-bold hover:text-yellow transition-all duration-200 ease-in-out"
              onClick={() => {
                trackEvent("click_menu", "inicio")
                setOpen((active) => !active)
              }}
            >
              INÍCIO
            </Link>
          </li>
          <li className="lg:w-auto w-full text-center">
            <Link
              to="#o-premio"
              title="O prêmio"
              className="text-white lg:text-base text-lg font-bold hover:text-yellow transition-all duration-200 ease-in-out"
              onClick={() => {
                trackEvent("click_menu", "o-premio")
                setOpen((active) => !active)
              }}
            >
              O PRÊMIO
            </Link>
          </li>
          <li className="lg:w-auto w-full text-center">
            <Link
              to="#manifestacoes"
              title="Artistas selecionados"
              className="text-white lg:text-base text-lg font-bold hover:text-yellow transition-all duration-200 ease-in-out"
              onClick={() => {
                trackEvent("click_menu", "o-feat-dos-sonhos")
                setOpen((active) => !active)
              }}
            >
              ARTISTAS SELECIONADOS
            </Link>
          </li>

          {user && (
            <li className="lg:w-auto w-full text-center">
              <button
                onClick={() => {
                  trackEvent("click_menu", "logout")
                  logout()
                }}
                title="Logout"
                className="text-white lg:text-base text-lg font-bold hover:text-yellow transition-all duration-200 ease-in-out"
                type="button"
              >
                LOGOUT
              </button>
            </li>
          )}

          {!user && (
            <>
              <li className="lg:w-auto w-full text-center">
                <a
                  href={FORM_ROUTES.login}
                  title="Login"
                  className="text-white lg:text-base text-lg font-bold hover:text-yellow transition-all duration-200 ease-in-out"
                  onClick={() => {
                    trackEvent("click_menu", "login")
                    setOpen((active) => !active)
                  }}
                >
                  LOGIN
                </a>
              </li>
            </>
          )}
          <li className="lg:w-auto w-full text-center">
            <button
              type="button"
              title="Alterar contraste"
              className={twJoin(
                btnActive ? "text-yellow" : "text-white",
                "lg:border-l lg:border-t-0 border-t border-sand lg:pl-8 lg:pt-0 pt-8 lg:w-auto w-full flex items-center flex-col lg:gap-0 gap-1 text-xl hover:text-yellow transition-all duration-200 ease-in-out",
              )}
              onClick={onDarkMode}
            >
              <span className="text-white lg:hidden text-lg font-bold">CONTRASTE</span>
              <IoIosContrast className="lg:text-lg text-2xl" />
            </button>
          </li>
          <li className="lg:w-auto w-full text-center">
            <button
              type="button"
              title="Alterar tamanho da fonte"
              className="w-full flex items-center flex-col lg:gap-0 gap-1"
              onClick={changeFontSize}
            >
              <span className="text-white lg:hidden text-lg font-bold">TAMANHO DA FONTE</span>
              <FontIcon className="text-white lg:text-[1.6rem] text-3xl hover:text-yellow transition-all duration-200 ease-in-out" />
            </button>
          </li>
        </ul>
      </nav>

      <button
        title="Abrir menu"
        onClick={() => setOpen((active) => !active)}
        className="bg-green-dark py-4 px-3 rounded-full lg:hidden block fixed right-4 top-4 dark:bg-green-dark2 dark:border-2"
      >
        <div
          className={twJoin(
            open && "transform rotate-45 top-[10px]",
            "w-[32px] h-[4px] bg-white mb-[6px] rounded-sm opacity-100 relative transition-all duration-300 ease-in-out",
          )}
        ></div>
        <div
          className={twJoin(
            open ? "transparent" : "bg-white",
            "w-[32px] h-[4px] mb-[6px] rounded-sm opacity-100 relative transition-all duration-300 ease-in-out",
          )}
        ></div>
        <div
          className={twJoin(
            open && "transform -rotate-45 bottom-[10px]",
            "w-[32px] h-[4px] bg-white mb-0 rounded-sm opacity-100 relative transition-all duration-300 ease-in-out",
          )}
        ></div>
      </button>
    </header>
  )
}
